import React, { useEffect, useState } from 'react';
import {Tooltip as ReactTooltip} from "react-tooltip";

import {EMPLOYEES} from '../../../../../../utils/constants/api';
import {VALIDATION_ERROR} from '../../../../../../utils/constants/errors';
import {fieldAutoResizer} from '../../../../../../utils/helpers/EmployeeProfile/fieldAutoResizer';
import {formatPhoneNumber} from '../../../../../../utils/helpers/EmployeeProfile/formatPhoneNumber';

import {EmployeeFromApi} from '../../../../../../shared/Employee/data/EmployeeFromApi';
import {EmergencyContactFromApi} from '../../../../../../shared/Employee/data/EmergencyContactFromApi';
import {ValidationError} from '../../../../../../shared/Common/Error/ValidationError';
import {AuthEmployee} from "../../../../../../shared/Security/data/AuthEmployee";

import TextField, {Type} from '../../../../../Component/Input/TextField/TextField';

import emergency from '../../../../../../assets/images/ProfileIcons/emergency.svg';

import './EmergencyContact.scss';
import {ROLE_ADMIN, ROLE_HR} from "../../../../../../shared/Security/constants/AccessLevels";


type EmergencyContact = {
    employee: EmployeeFromApi,
    authEmployee: AuthEmployee,
    isMyProfile: boolean | null,
    isProfileOfMyHierarchyChain: boolean | null,
    setLoadingBarProgress: (progress: number) => void
}

function EmergencyContact(props: EmergencyContact) {
    const [emergencyPerson, setEmergencyPerson] = useState<string>('');
    const [emergencyPersonError, setEmergencyPersonError] = useState<string | null>(null);

    const [emergencyName, setEmergencyName] = useState<string>('');
    const [emergencyNameError, setEmergencyNameError] = useState<string | null>(null);

    const [emergencyPhone, setEmergencyPhone] = useState<string>('');
    const [emergencyPhoneFocused, setEmergencyPhoneFocused] = useState<boolean>(false);
    const [emergencyPhoneError, setEmergencyPhoneError] = useState<string | null>(null);

    useEffect(() => {
        props.setLoadingBarProgress(50);

        fetch(EMPLOYEES + '/employee/' + props.employee.id + '/emergency-contact/', {
            method: 'GET',
            headers: { 'Accept': 'application/json', 'Content-type': 'application/json' }
        })
            .then(response => response.json())
            .then((contact: EmergencyContactFromApi) => {
                setEmergencyPerson(contact.person);
                fieldAutoResizer('emergency-person', contact.person ? contact.person : '');
                setEmergencyName(contact.name);
                fieldAutoResizer('emergency-name', contact.name ? contact.name : '');
                setEmergencyPhone(contact.phone ? '+' + formatPhoneNumber(contact.phone) : '');
                fieldAutoResizer('emergency-phone', contact.phone ? formatPhoneNumber(contact.phone) : '');
            })
            .finally(() => props.setLoadingBarProgress(100));
    }, [])

    function setValue(value: any, field: any) {
        if (!value) {
            value = null;
        }
        resetAll();
        fetch(EMPLOYEES + '/employee/' + props.employee.id + '/emergency-contact/' + field + '/' + encodeURIComponent(value), {
            method: 'PUT',
        }).then(response => {
            if (response.status === VALIDATION_ERROR) {
                response.json().then((errors: ValidationError[]) => validate(errors));
            }
        });
    }

    function resetAll(): void {
        setEmergencyPersonError(null);
        setEmergencyNameError(null);
        setEmergencyPhoneError(null);
    }

    function validate(errors: ValidationError[]): void {
        errors.forEach((error: ValidationError) => {
            switch (error.property) {
                case 'employee.person':
                    setEmergencyPersonError(error.message);
                    break;
                case 'employee.name':
                    setEmergencyNameError(error.message);
                    break;
                case 'employee.phone':
                    setEmergencyPhoneError(error.message);
                    break;
            }
        });
    }

    const roles = [ROLE_ADMIN, ROLE_HR];
    const hasRole = roles.some(role => props.authEmployee.roles.includes(role));

    return (
        <div className={'emergency-contact'}>
            <div className={'info'}>
                <div className={'info-header'}>
                    <div className={'emergency-icon'}>
                        <img src={emergency}></img>
                    </div>
                    <h3>Contact Person in Case of Emergency</h3>
                </div>
                <div className={'info-body'}>
                    { !emergencyPerson || (emergencyPerson && emergencyPerson.length < 2) || !emergencyName || (emergencyName && emergencyName.length < 2) || !emergencyPhone || (emergencyPhone && emergencyPhone.length < 14) ?
                    <div className={'help-text'}>
                        <i className={'material-icons'}>info</i>
                        <p>Add a relationship, name and phone number of someone who should be contacted if you have an emergency.</p>
                    </div> : null }
                    <div className={'profile-field'}>
                        <div className={'profile-field-title'}>
                            <p>Contact person</p>
                            <svg id="emergency-title" className={'tooltip-help'} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10.0834 14.854C10.3056 14.854 10.4931 14.7776 10.6459 14.6248C10.7987 14.4721 10.8751 14.2846 10.8751 14.0623C10.8751 13.8401 10.7987 13.6526 10.6459 13.4998C10.4931 13.3471 10.3056 13.2707 10.0834 13.2707C9.86119 13.2707 9.67369 13.3471 9.52091 13.4998C9.36814 13.6526 9.29175 13.8401 9.29175 14.0623C9.29175 14.2846 9.36814 14.4721 9.52091 14.6248C9.67369 14.7776 9.86119 14.854 10.0834 14.854ZM9.35425 11.8123H10.5834C10.5834 11.4512 10.6286 11.1214 10.7188 10.8228C10.8091 10.5241 11.0904 10.1804 11.5626 9.7915C11.9931 9.43039 12.2987 9.07623 12.4792 8.729C12.6598 8.38178 12.7501 7.99984 12.7501 7.58317C12.7501 6.84706 12.5105 6.25678 12.0313 5.81234C11.5522 5.36789 10.9167 5.14567 10.1251 5.14567C9.44452 5.14567 8.84383 5.31581 8.323 5.65609C7.80216 5.99636 7.42369 6.46511 7.18758 7.06234L8.29175 7.479C8.44452 7.09011 8.67369 6.78803 8.97925 6.57275C9.2848 6.35748 9.64591 6.24984 10.0626 6.24984C10.5348 6.24984 10.9167 6.37831 11.2084 6.63525C11.5001 6.8922 11.6459 7.22206 11.6459 7.62484C11.6459 7.93039 11.5556 8.21859 11.3751 8.48942C11.1945 8.76025 10.9306 9.0415 10.5834 9.33317C10.1667 9.69428 9.85772 10.0519 9.65633 10.4061C9.45494 10.7603 9.35425 11.229 9.35425 11.8123ZM10.0001 18.3332C8.86119 18.3332 7.7848 18.1144 6.77091 17.6769C5.75703 17.2394 4.87161 16.6422 4.11466 15.8853C3.35772 15.1283 2.7605 14.2429 2.323 13.229C1.8855 12.2151 1.66675 11.1387 1.66675 9.99984C1.66675 8.84706 1.8855 7.76373 2.323 6.74984C2.7605 5.73595 3.35772 4.854 4.11466 4.104C4.87161 3.354 5.75703 2.76025 6.77091 2.32275C7.7848 1.88525 8.86119 1.6665 10.0001 1.6665C11.1529 1.6665 12.2362 1.88525 13.2501 2.32275C14.264 2.76025 15.1459 3.354 15.8959 4.104C16.6459 4.854 17.2397 5.73595 17.6772 6.74984C18.1147 7.76373 18.3334 8.84706 18.3334 9.99984C18.3334 11.1387 18.1147 12.2151 17.6772 13.229C17.2397 14.2429 16.6459 15.1283 15.8959 15.8853C15.1459 16.6422 14.264 17.2394 13.2501 17.6769C12.2362 18.1144 11.1529 18.3332 10.0001 18.3332ZM10.0001 17.0832C11.9723 17.0832 13.6459 16.3922 15.0209 15.0103C16.3959 13.6283 17.0834 11.9582 17.0834 9.99984C17.0834 8.02761 16.3959 6.354 15.0209 4.979C13.6459 3.604 11.9723 2.9165 10.0001 2.9165C8.04175 2.9165 6.37161 3.604 4.98966 4.979C3.60772 6.354 2.91675 8.02761 2.91675 9.99984C2.91675 11.9582 3.60772 13.6283 4.98966 15.0103C6.37161 16.3922 8.04175 17.0832 10.0001 17.0832Z" fill="#6A7885"/>
                            </svg>
                            <ReactTooltip
                                anchorId="emergency-title"
                                place="bottom"
                                content={'Indicate a relationship to a contact person, e.g. mother, husband, sister etc.'}
                                noArrow={true}
                            />
                        </div>
                        <div className={'profile-field-value'}>
                            <TextField
                                label=''
                                placeholder="—"
                                id={'emergency-person'}
                                className={`emergency-person ${ (!hasRole && !props.isMyProfile) ? 'default-pointer' : ''}`}
                                required={ false }
                                value={ emergencyPerson }
                                type={ Type.Textarea }
                                horizontal={ false }
                                error={ emergencyPersonError }
                                onChange={ value => {
                                    const isValid = /^[a-zA-Zа-яА-ЯіІїЇєЄґҐ\s\\'()\-]*$/.test(value);
                                    if (isValid) {
                                        fieldAutoResizer('emergency-person', value)
                                        setEmergencyPerson(value.charAt(0).toUpperCase() + value.slice(1));
                                        resetAll();
                                    }
                                }}
                                onBlur={() => {
                                    setValue(emergencyPerson, 'person')
                                }}
                                maxLength={100}
                                disabled={ (!hasRole && !props.isMyProfile) || props.employee.isDeleted }
                            />
                        </div>
                    </div>

                    <div className={'profile-field'}>
                        <div className={'profile-field-title'}>
                            <p>Name of contact person</p>
                        </div>
                        <div className={'profile-field-value'}>
                            <TextField
                                label=''
                                className={`emergency-name ${ (!hasRole && !props.isMyProfile) ? 'default-pointer' : ''}`}
                                id={'emergency-name'}
                                placeholder="—"
                                required={ false }
                                value={ emergencyName }
                                type={ Type.Textarea }
                                horizontal={ false }
                                error={ emergencyNameError }
                                onChange={ value => {
                                    const isValid = /^[a-zA-Zа-яА-ЯіІїЇєЄґҐ\s\\'()\-]*$/.test(value);
                                    if (isValid) {
                                        setEmergencyName(value.charAt(0).toUpperCase() + value.slice(1));
                                        fieldAutoResizer('emergency-name', value)
                                        resetAll();
                                    }
                                }}
                                onBlur={() => {
                                    setValue(emergencyName, 'name')
                                }}
                                maxLength={100}
                                disabled={ (!hasRole && !props.isMyProfile) || props.employee.isDeleted }
                            />
                        </div>
                    </div>

                    <div className={'profile-field'}>
                        <div className={'profile-field-title'}>
                            <p>Contact number</p>
                        </div>
                        <div className={'profile-field-value'}>
                            <div
                                onFocus={() => {
                                    if (emergencyPhone === '') {
                                        setEmergencyPhone('+');
                                    }
                                    setEmergencyPhoneFocused(true)
                                }}
                                onBlur={() => {
                                    setEmergencyPhoneFocused(false)
                                    if (emergencyPhone === '+') {
                                        setEmergencyPhone('');
                                    }
                                }}>
                                <TextField
                                    label=""
                                    placeholder="—"
                                    required={false}
                                    id={'emergency-phone'}
                                    className={`emergency-phone ${ (!hasRole && !props.isMyProfile) ? 'default-pointer' : ''}`}
                                    value={formatPhoneNumber(emergencyPhone, emergencyPhoneFocused)}
                                    type={Type.Textarea}
                                    horizontal={false}
                                    error={emergencyPhoneError}
                                    onChange={(event) => {
                                        const input = document.getElementById('emergency-phone') as HTMLInputElement;
                                        const cursorPosition = input.selectionStart;
                                        const formatted = formatPhoneNumber(event, emergencyPhoneFocused);
                                        fieldAutoResizer('emergency-phone', formatted)
                                        setTimeout(() => {
                                            if (cursorPosition !== null) {
                                                let cursorFix = 0;
                                                if (cursorPosition === 0 || ((cursorPosition === 2 || cursorPosition === 5 || cursorPosition === 8 || cursorPosition === 12 || cursorPosition === 15) && cursorPosition === event.length)) {
                                                    cursorFix = 1;
                                                }
                                                input.setSelectionRange(cursorPosition + cursorFix, cursorPosition + cursorFix);
                                            }
                                        }, 0);
                                        setEmergencyPhone(formatted);
                                        resetAll();
                                    }}
                                    onBlur={() => {
                                        if (emergencyPhone === '' || emergencyPhone === '+') {
                                            setEmergencyPhone('');
                                            setValue('', 'phone')
                                        } else {
                                            setValue(formatPhoneNumber(emergencyPhone, false), 'phone')
                                        }
                                        setEmergencyPhoneFocused(false)
                                    }}
                                    onFocus={(event:string) => {
                                        if (event === '') {
                                            setEmergencyPhone('+');
                                        }
                                        setEmergencyPhoneFocused(true)
                                    }}
                                    maxLength={17}
                                    disabled={ (!hasRole && !props.isMyProfile) || props.employee.isDeleted }
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EmergencyContact;